import React, { useEffect } from "react";
import { ReviewFormData } from "./types";
import { reviewNotes } from "../../utils/review";
import styles from "../../components/RatingNumber/styles.module.css";
import { Button } from "../ui";
import {
  Mail,
  MessageCircleMore,
  MinusCircle,
  Pencil,
  PlusCircle,
  Send,
} from "lucide-react";
import nl2br from "../../utils/nl2br";
import { trackEvent } from "../../utils/analytics";
import ReferralBlock from "../ReferralBlock";

type SummaryStepProps = {
  review: ReviewFormData;
  onPrevious: () => void;
  onSubmit: () => void;
  errors?: string[];
  isSubmitting?: boolean;
};

const SummaryStep: React.FC<SummaryStepProps> = (props) => {
  const { review, errors, isSubmitting, onSubmit, onPrevious } = props;

  useEffect(() => {
    trackEvent({
      name: "checkout_progress",
      params: {
        event_name: "form_step_completed",
        page_type: "review_form",
        step_index: 3,
        step_name: "summary",
      },
    });
  }, []);

  return (
    <>
      <div>
        <h2 className="text-2xl font-bold">Récapitulatif</h2>
        <p className="text-sm text-gray-500 mb-4">
          Vous êtes sur le point d'envoyer votre avis.
        </p>
      </div>
      {errors && errors.length > 0 && (
        <div className="flex flex-col gap-2 p-4 mb-4 bg-red-100 text-white border-red-500 border rounded-md">
          {errors.map((error) => (
            <span className="text-sm text-red-500">{error}</span>
          ))}
        </div>
      )}
      {errors?.some((e) => e.includes("Immatriculation")) && review.token && (
        <ReferralBlock token={review.token} />
      )}
      <div className="flex flex-col">
        {reviewNotes.map((note, index) => {
          const isLast = index === reviewNotes.length - 1;
          return (
            <div
              key={note.name}
              className={`flex items-center justify-between gap-2 border-b py-2 ${isLast ? "border-b-0" : ""}`}
            >
              <span className="flex items-center gap-2">
                {note.icon}
                <span className="text-md font-bold">{note.label}</span>
              </span>
              <span
                className={`${styles.ratingCheck} ${styles.selected} ${styles.small}`}
              >
                {review[note.name as keyof ReviewFormData]}
              </span>
            </div>
          );
        })}
        {review.comment && review.comment.length > 0 && (
          <div className={`flex flex-col gap-2 py-2`}>
            <span className="flex items-center gap-2 text-md font-bold">
              <MessageCircleMore className="size-5" />
              Commentaire
            </span>
            <span className="text-md">{nl2br(review.comment)}</span>
          </div>
        )}
        {review.tops && review.tops.length > 0 && (
          <div className={`flex flex-col gap-2 py-2`}>
            <span className="flex items-center gap-2 text-md font-bold">
              <PlusCircle className="size-5 stroke-green-500" />
              Avantages
            </span>
            <span className="text-md">{nl2br(review.tops)}</span>
          </div>
        )}
        {review.flops && review.flops.length > 0 && (
          <div className={`flex flex-col gap-2 py-2`}>
            <span className="flex items-center gap-2 text-md font-bold">
              <MinusCircle className="size-5 stroke-red-500" />
              Inconvénients
            </span>
            <span className="text-md">{nl2br(review.flops)}</span>
          </div>
        )}
        {review.email && review.email.length > 0 && (
          <div className={`flex flex-col gap-2 py-2`}>
            <span className="flex items-center gap-2 text-md font-bold">
              <Mail className="size-5" />
              Email
            </span>
            <span className="text-md">{review.email}</span>
          </div>
        )}
      </div>
      <div className="flex gap-4 sticky bottom-0 bg-white py-4 w-full">
        <Button
          variant="outline"
          className="mt-4 flex-grow-1"
          onClick={onPrevious}
        >
          <Pencil className="size-4" />
          Editer mes notes
        </Button>
        <Button
          type="submit"
          variant="default"
          className="mt-4 flex-grow-1"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          <Send className="size-4" />
          {isSubmitting ? "Envoi en cours..." : "Confirmer et envoyer"}
        </Button>
      </div>
    </>
  );
};

export default SummaryStep;
